import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getWorksheetPersistentStorage from 'notebook/notes/WorksheetPersistentStorage';
import {
  DisplayNameToStatusTypeMapping,
  DocumentStatus,
  MethodTaskData,
  StepTaskOrInaccessible,
  TaskData,
  Transition,
  Worksheet,
  WorksheetStepDocumentSelection,
  WorksheetTaskLoadingStateMapping,
  WorksheetTaskStatusType,
} from 'notebook/worksheets/WorksheetConstants';
import {
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode_methodTask_ProcessTaskNode as MethodTask,
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode as PMEI,
} from 'procedures/worksheets/__generated/PMEIsQuery';
import * as DateUtil from 'util/DateUtil';

/**
 * Converts an array of possible transitions into a dicitionary where the key is the status display name and
 * the value is the corresponding status ID
 * @param transitions Array of transitions to convert
 * @returns Dict mapping status display names to status ID
 */
export function convertTransitionsToStatusMap(transitions: Array<Transition>): Record<string, string> {
  return Object.fromEntries(
    transitions.map((transition: Transition) => [
      transition.outStatus.displayName.toUpperCase(),
      transition.outStatus.id,
    ])
  );
}

function _extractTasksData(
  tasks: Array<StepTaskOrInaccessible> | Array<{task: MethodTask; pmei: PMEI}>,
  extractorFn: (
    value: {task: MethodTask; pmei: PMEI} | StepTaskOrInaccessible | null | undefined
  ) => MethodTaskData | TaskData
): TaskData {
  const allTaskData = tasks.map((task) => extractorFn(task));
  const allSameStatus = allTaskData.every((task) => task.status === allTaskData[0].status);
  const allTaskIds = allTaskData.flatMap((task) => task.taskIds ?? []);
  if (allSameStatus) {
    return allTaskData[0] ? {...allTaskData[0], taskIds: allTaskIds} : {};
  } else {
    const taskData = {...allTaskData[0], taskIds: allTaskIds, status: WorksheetTaskStatusType.NOT_READY};
    // If any of the tasks is mapped to a terminal status, but the others are not at the same state, show the
    // loading status correspoding to the terminal status, i.e. if one task is completed, we expect the others
    // to also be reaching completion. So, we display "completing..."
    for (const task of allTaskData) {
      if (task.status && WorksheetTaskLoadingStateMapping[task.status]) {
        return {...taskData, status: WorksheetTaskLoadingStateMapping[task.status]};
      }
    }
    return taskData;
  }
}

export interface StepTaskSelection {
  /** The tasks whose statuses make up the step's status. */
  statusTasks: Array<StepTaskOrInaccessible>;
  /** The tasks the step's transitions update. */
  transitionTasks: Array<StepTaskOrInaccessible>;
}

export type SelectStepTasks = (tasks: Array<StepTaskOrInaccessible>) => StepTaskSelection;

/** Derives a step's status and the task IDs its transitions update. */
export function extractStepTasksData({statusTasks, transitionTasks}: StepTaskSelection): TaskData {
  return {
    ..._extractTasksData(statusTasks, extractStepTaskData),
    taskIds: transitionTasks.flatMap((task) => (isInaccessible(task) ? [] : [task.id])),
  };
}

export function extractMethodTasksData(
  pmeisAndTasks: Array<{task: MethodTask | null; pmei: PMEI}> = [],
  isDelayedMappingEnabled: boolean = false
): MethodTaskData {
  if (isDelayedMappingEnabled && pmeisAndTasks.length > 0 && pmeisAndTasks.every((pt) => pt.task == null)) {
    return {status: WorksheetTaskStatusType.PENDING};
  }
  const withTasks = pmeisAndTasks.filter((pt): pt is {task: MethodTask; pmei: PMEI} => pt.task != null);
  return _extractTasksData(withTasks, extractMethodTaskData);
}

/**
 * Returns whether every PMEI of a worksheet has a linked method task.
 *
 * In delayed mapping (split/pool) scenarios, a PMEI's method task is created asynchronously after the run is
 * started, so this list may contain PMEIs with `task == null` until mapping completes. This is used to know
 * when the asynchronous task creation kicked off by "Start run" has finished for the current worksheet.
 *
 * Returns false for an empty list, since there are no tasks to have been created yet.
 */
export function areAllMethodTasksPresent(
  pmeisAndTasks: Array<{task: MethodTask | null; pmei: PMEI}>
): boolean {
  return pmeisAndTasks.length > 0 && pmeisAndTasks.every((pt) => pt.task != null);
}

export function extractMethodTaskData({task, pmei}: {task: MethodTask; pmei: PMEI}): MethodTaskData {
  if (task == null || isInaccessible(task)) {
    return {};
  }
  const procedureType = pmei.procedureRun.procedureType;
  const timestamp = pmei.taskTransitionExtensions.find(
    (ext) => ext.statusDisplayName === task.status.displayName
  )?.timestampUtcIsoformat;

  return {
    ..._extractTaskData(task),
    procedureType,
    timestamp: DateUtil.readableTime(timestamp ?? new Date(task.lastStatusChange.createdAt)),
  };
}

export function extractStepTaskData(task?: StepTaskOrInaccessible | null): TaskData {
  return _extractTaskData(task);
}

function _extractTaskData(task?: StepTaskOrInaccessible | MethodTask | null): TaskData {
  if (task == null || isInaccessible(task)) {
    return {};
  }

  return {
    status: DisplayNameToStatusTypeMapping[task.status.displayName],
    outStatuses: !isInaccessible(task.taskSchema)
      ? convertTransitionsToStatusMap(task.taskSchema.statusLifecycle.transitions)
      : undefined,
    taskIds: [task.id],
    lastEditedUser: task.lastStatusChange.creator,
    timestamp: DateUtil.readableTime(new Date(task.lastStatusChange.createdAt)),
  };
}

/**
 * Get the selected step ID
 * If step ID is found in the URL hash, use that step ID. Otherwise, use the persisted step ID
 * @param worksheetId
 * @returns the ID of the step worksheet should initialize
 */
export const getSelectedStepId = (worksheetId: string, worksheetStepIds: Array<string>): string | null => {
  const urlHash = window.location.hash.slice(1);
  if (urlHash && worksheetStepIds.includes(urlHash)) {
    return urlHash;
  }
  const stepId = getWorksheetPersistentStorage().getPersistedStepId(worksheetId);
  return stepId;
};

/**
 * Set the selected step ID into the URL hash and persisted storage
 * @param stepId the step ID to set
 */
export const setSelectedStepId = (worksheetId: string, stepId?: string): void => {
  if (stepId) {
    window.location.hash = `#${stepId}`;
  }
  getWorksheetPersistentStorage().setSelectedStepId(worksheetId, stepId);
};

/**
 * Get the selectedStep given stepId.
 * If no steps in worksheet correspond to given stepId, return the first available step
 * If there are no step-groups with an available step, return undefined for stepGroupIndex & stepIndex
 * @param stepId ID of the selected step or undefined
 * @param worksheet worksheet data
 * @returns WorksheetStepDocumentSelection object that has step ID and document object
 */
export function getSelectedStepFromStepId(
  stepId: string | undefined,
  worksheet: Worksheet
): WorksheetStepDocumentSelection {
  // find the stepGroupIndex that contains the step with stepId
  const stepGroupIndexWithStepId = worksheet.worksheetStepGroups.findIndex((stepGroup) =>
    stepGroup.worksheetSteps.some((step) => step.document.id === stepId)
  );

  // when stepId is not provided or no valid stepGroupIndex found,
  // return the first step of the first step group with at least one step
  if (stepId === undefined || stepGroupIndexWithStepId === -1) {
    const indexFirstStepGroupWithStep = worksheet.worksheetStepGroups.findIndex(
      (stepGroup) => stepGroup.worksheetSteps.length > 0
    );
    return {
      stepGroupIndex: indexFirstStepGroupWithStep !== -1 ? indexFirstStepGroupWithStep : undefined,
      stepIndex: indexFirstStepGroupWithStep !== -1 ? 0 : undefined,
      document: {status: DocumentStatus.NOT_SELECTED},
    };
  }

  // find the corresponding stepIndex for the step matching stepId
  const stepGroup = worksheet.worksheetStepGroups[stepGroupIndexWithStepId];
  const stepIndexWithStepId = stepGroup.worksheetSteps.findIndex((step) => step.document.id === stepId);

  return {
    stepGroupIndex: stepGroupIndexWithStepId,
    stepIndex: stepIndexWithStepId,
    document: {status: DocumentStatus.NOT_SELECTED},
  };
}
